import React, { useCallback } from 'react'
import { ProgressReportByProcessPageConfig } from '../../../../../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { PageValidationError } from '../../../../../../../../../domain/entity/version-report/VersionReportTemplateEntity/validator'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../../../../../../lib/functions/workload'
import { ProjectPlanTemplateSelect } from '../../../../ProjectPlanSelect'
import { usePageConfigHandlers } from '../hooks/usePageConfigHandlers'
import {
  AggregateFieldToggleGroup,
  AggregateTargetToggleGroup,
  WorkloadUnitToggleGroup,
} from '../../../../../../../../components/toggleGroups'
import {
  ConfigContainer,
  SectionContent,
  FormSection,
  FormLabel,
  PageTitleInput,
  ProjectPlanSelectWrapper,
  ToggleGroupWrapper,
  ErrorBanner,
  ErrorMessages,
  ErrorMessage,
} from '../styles'
import { VersionReportTemplatePageFormModel } from '../../../model/VersionReportTemplatePageFormModel'

type ConfigFormProps = {
  page: VersionReportTemplatePageFormModel
  projectUuid: string
  validationErrors?: PageValidationError[]
  onUpdate: (
    pageNumber: number,
    updater: (
      prev: VersionReportTemplatePageFormModel
    ) => VersionReportTemplatePageFormModel
  ) => void
}

export const ProgressReportByProcessConfigForm: React.FC<ConfigFormProps> = ({
  page,
  projectUuid,
  validationErrors,
  onUpdate,
}) => {
  const config = page.config as ProgressReportByProcessPageConfig

  const { handlePageTitleChange } = usePageConfigHandlers(page, onUpdate)

  const handleAggregateFieldChange = useCallback(
    (value: AggregateField) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByProcessPageConfig

        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            aggregateField: value,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  const handleAggregateTargetChange = useCallback(
    (value: AggregateTarget) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByProcessPageConfig

        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            aggregateTarget: value,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  const handleWorkloadUnitChange = useCallback(
    (value: WorkloadUnit) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByProcessPageConfig

        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            workloadUnit: value,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  const handleTableRowsChange = useCallback(
    (uuid: string, selected: boolean) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByProcessPageConfig

        const updatedTableRows = selected
          ? currentConfig.tableRows.includes(uuid)
            ? currentConfig.tableRows
            : [...currentConfig.tableRows, uuid]
          : currentConfig.tableRows.filter(id => id !== uuid)

        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            tableRows: updatedTableRows,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  return (
    <ConfigContainer>
      {validationErrors && validationErrors.length > 0 && (
        <ErrorBanner>
          <ErrorMessages>
            {validationErrors.map((error, index) => (
              <ErrorMessage key={index}>• {error.message}</ErrorMessage>
            ))}
          </ErrorMessages>
        </ErrorBanner>
      )}
      <SectionContent>
        <FormSection>
          <PageTitleInput
            label="ページタイトル"
            value={page.pageTitle}
            onChange={handlePageTitleChange}
            placeholder="ページのタイトルを入力してください"
            variant="outlined"
            required={true}
          />
        </FormSection>
        <FormSection>
          <FormLabel>集計項目</FormLabel>
          <ToggleGroupWrapper>
            <AggregateFieldToggleGroup
              value={config.aggregateField}
              onChange={handleAggregateFieldChange}
            />
          </ToggleGroupWrapper>
        </FormSection>

        <FormSection>
          <FormLabel>集計対象</FormLabel>
          <ToggleGroupWrapper>
            <AggregateTargetToggleGroup
              value={config.aggregateTarget}
              onChange={handleAggregateTargetChange}
            />
          </ToggleGroupWrapper>
        </FormSection>

        <FormSection>
          <FormLabel>工数単位</FormLabel>
          <ToggleGroupWrapper>
            <WorkloadUnitToggleGroup
              value={config.workloadUnit}
              refresh={handleWorkloadUnitChange}
              disabled={
                config.aggregateField !== AggregateField.WBS_ITEM_WORKLOAD
              }
            />
          </ToggleGroupWrapper>
        </FormSection>

        <FormSection>
          <FormLabel>集計対象の工程を選択してください</FormLabel>
          <ProjectPlanSelectWrapper>
            <ProjectPlanTemplateSelect
              key={page.pageNumber}
              projectUuid={projectUuid}
              selectedUuids={config.tableRows}
              updateSelectedUuid={handleTableRowsChange}
            />
          </ProjectPlanSelectWrapper>
        </FormSection>
      </SectionContent>
    </ConfigContainer>
  )
}
